// The "elements" arm of checkout_ui_20260913: a payment page on our own domain
// that deliberately mirrors Stripe's hosted Checkout. The experiment measures
// the architecture, so anything that looks different from hosted is a confound.
//
// Written against the basil Stripe.js that layouts/checkout loads, where
// initCheckout takes fetchClientSecret and resolves to the Checkout object.
// Every amount on the page comes from the Session object's preformatted fields:
// with Adaptive Pricing a Canadian visitor's session is in CAD, which Rails
// never sees.

import ahoy from './ahoy';

declare const Stripe: any;

type FormattedAmount = { amount: string; minorUnitsAmount: number };

const APPEARANCE = {
  theme: 'stripe',
  variables: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Ubuntu, sans-serif',
    fontSizeBase: '14px',
    colorPrimary: '#0074d4',
    colorText: 'rgba(26, 26, 26, 0.9)',
    colorTextSecondary: 'rgba(26, 26, 26, 0.6)',
    borderRadius: '6px',
  },
  rules: {
    // Hosted marks the chosen currency with a dark border, not a focus ring.
    '.ToggleItem--selected': { borderColor: 'rgba(26, 26, 26, 0.9)', boxShadow: 'none' },
    // Hosted's payment method rows: regular-weight labels that stay dark when chosen.
    '.AccordionItem': { fontSize: '14px', fontWeight: '400', color: 'rgba(26, 26, 26, 0.9)' },
    '.AccordionItem--selected': { color: 'rgba(26, 26, 26, 0.9)' },
  },
};

const LOAD_ERROR = 'Something went wrong loading checkout. Please refresh the page.';

document.addEventListener('DOMContentLoaded', () => {
  const root = document.getElementById('checkoutRoot');
  if (!root) return;

  initCheckoutPage(root).catch((error) => {
    console.error(error);
    track(root, 'Checkout Load Failed', { error: String(error?.message ?? error) });
    showError(root, LOAD_ERROR);
  });
});

async function initCheckoutPage(root: HTMLElement) {
  const { publishableKey, clientSecret, locale } = root.dataset;
  if (typeof Stripe === 'undefined' || !publishableKey || !clientSecret) {
    throw new Error('Checkout is missing Stripe.js, a publishable key or a client secret');
  }

  const stripe = Stripe(publishableKey, { locale: locale || 'auto' });
  const checkout = await stripe.initCheckout({
    fetchClientSecret: () => Promise.resolve(clientSecret),
    elementsOptions: { appearance: APPEARANCE },
    adaptivePricing: { allowed: true },
  });

  let currencySelectorMounted = false;
  // checkout_summary_20261003: "new" is the registration panel, anything else
  // the hosted-style summary.
  const render = root.dataset.summary === 'new' ? renderRegistrationSummary : renderSummary;
  const onSession = (session: any) => {
    render(root, session);
    // Stripe requires the Currency Selector wherever Adaptive Pricing is used
    // with Elements. Like hosted, it only appears for visitors offered a local
    // currency.
    if (!currencySelectorMounted && session.currencyOptions?.length) {
      checkout.createCurrencySelectorElement().mount('#currency-selector');
      currencySelectorMounted = true;
    }
  };
  onSession(checkout.session());
  checkout.on('change', onSession);

  // The accordion's open row, so a Pay press records what was being paid with.
  let paymentMethod: string | undefined;

  const divider = root.querySelector<HTMLElement>('[data-checkout-divider]')!;
  // Hosted lays its wallet buttons out side by side at 55px, and offers Apple
  // Pay (by QR code) in browsers other than Safari too.
  const expressCheckout = checkout.createExpressCheckoutElement({
    buttonHeight: 55,
    layout: { maxColumns: 2, maxRows: 1, overflow: 'auto' },
    paymentMethods: { applePay: 'always' },
  });
  expressCheckout.on('ready', ({ availablePaymentMethods }: any) => {
    divider.hidden = !availablePaymentMethods;
  });
  expressCheckout.on('confirm', async (event: any) => {
    hideError(root);
    const expressPaymentType = event.expressPaymentType;
    // The open row too: a wallet button can be pressed with, say, Affirm selected.
    track(root, 'Checkout Pay Pressed', { payment_method: expressPaymentType, selected_payment_method: paymentMethod });
    const result = await checkout.confirm({ expressCheckoutConfirmEvent: event });
    if (result.type === 'error') {
      trackPaymentError(root, expressPaymentType, result.error);
      showError(root, result.error.message);
    }
  });
  expressCheckout.mount('#express-checkout');

  // Hosted always asks for the cardholder's name, and for the full billing
  // address too when tax is collected (the session is created with
  // billing_address_collection "required" then). On taxed sessions the Billing
  // Address Element collects both, so the Payment Element must not ask for them
  // a second time; otherwise the Payment Element adds the name to its own form.
  const taxEnabled = root.dataset.taxEnabled === 'true';
  const billingDetails = taxEnabled ? { name: 'never', address: 'never' } : { name: 'always' };

  // Hosted's accordion: one joined box with radio buttons, in this order.
  const paymentElement = checkout.createPaymentElement({
    layout: { type: 'accordion', radios: true, spacedAccordionItems: false },
    paymentMethodOrder: ['card', 'cashapp', 'affirm', 'klarna', 'us_bank_account'],
    fields: { billingDetails },
  });
  if (taxEnabled) {
    checkout.createBillingAddressElement().mount('#billing-address-element');
  }
  paymentElement.on('change', (event: any) => {
    paymentMethod = event.value?.type;
  });
  paymentElement.on('ready', () => {
    setLoading(root, false);
    track(root, 'Checkout Loaded');
  });
  // Stripe.js loaded but the element itself could not start, which otherwise
  // leaves a page with a permanently disabled Pay button and no explanation.
  paymentElement.on('loaderror', (event: any) => {
    track(root, 'Checkout Load Failed', { error: event.error?.type, error_code: event.error?.code });
    showError(root, LOAD_ERROR);
  });
  paymentElement.mount('#payment-element');

  const form = root.querySelector<HTMLFormElement>('[data-checkout-form]')!;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError(root);
    setLoading(root, true);
    track(root, 'Checkout Pay Pressed', { payment_method: paymentMethod });
    // On success Stripe redirects to the session's return_url (the shared
    // confirmation page), handling 3D Secure along the way.
    const result = await checkout.confirm();
    if (result.type === 'error') {
      trackPaymentError(root, paymentMethod, result.error);
      showError(root, result.error.message);
      setLoading(root, false);
    }
  });

  // Narrow screens have two toggles for the same summary: "Details" in the
  // header and "View details" under the heading, as hosted does.
  const toggles = root.querySelectorAll<HTMLButtonElement>('[data-checkout-details-toggle]');
  const details = root.querySelector<HTMLElement>('[data-checkout-details]')!;
  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const open = details.classList.toggle('co-details-open');
      toggles.forEach((each) => each.setAttribute('aria-expanded', String(open)));
    });
  });
}

function renderSummary(root: HTMLElement, session: any) {
  const lineItems: any[] = session.lineItems || [];
  const total = session.total || {};

  const businessName = session.businessName || 'mChip';
  const offersCurrencyChoice = session.currencyOptions?.length > 0;
  const single = lineItems.length === 1 ? lineItems[0] : null;
  const taxEnabled = root.dataset.taxEnabled === 'true';
  const discounted = total.discount?.minorUnitsAmount > 0;

  root.querySelector<HTMLElement>('[data-checkout-sandbox]')!.hidden = session.livemode !== false;

  // Hosted's summary has two shapes, reproduced by classes on .co-summary (the
  // CSS decides which pieces each shape and viewport shows):
  //  - co-hero: one untaxed, undiscounted item with no currency choice is shown
  //    as the product itself (name, amount, description, large image);
  //  - otherwise a line-item list with subtotal, tax and total.
  // With a currency choice the big amount gives way to the Currency Selector,
  // and the desktop heading reads "Pay <business>" instead of the item name.
  const summary = root.querySelector<HTMLElement>('.co-summary')!;
  summary.classList.toggle('co-hero', !!single && !taxEnabled && !discounted && !offersCurrencyChoice);
  summary.classList.toggle('co-single', !!single);
  summary.classList.toggle('co-currency-choice', offersCurrencyChoice);

  setText(root, '[data-checkout-label-product]', single?.name ?? '');
  setText(root, '[data-checkout-label-pay]', `Pay ${businessName}`);
  setText(root, '[data-checkout-hero-description]', single?.description ?? '');

  const heroImage = root.querySelector<HTMLImageElement>('[data-checkout-hero-image]')!;
  const heroImageSrc = single?.images?.[0];
  if (heroImageSrc && heroImage.getAttribute('src') !== heroImageSrc) heroImage.src = heroImageSrc;
  heroImage.hidden = !heroImageSrc;

  root.querySelector<HTMLElement>('[data-checkout-total]')!.hidden = offersCurrencyChoice;
  root.querySelector<HTMLElement>('[data-checkout-currency]')!.hidden = !offersCurrencyChoice;

  setText(root, '[data-checkout-total]', formatted(total.total));
  setText(root, '[data-checkout-total-due]', formatted(total.total));
  setText(root, '[data-checkout-subtotal]', formatted(total.subtotal));

  const list = root.querySelector<HTMLElement>('[data-checkout-lines]')!;
  list.replaceChildren(...lineItems.map(renderLineItem));
  root
    .querySelector<HTMLElement>('[data-checkout-details]')!
    .classList.toggle('co-details-with-images', lineItems.some((item) => item.images?.length));

  const discountRow = root.querySelector<HTMLElement>('[data-checkout-discount-row]')!;
  discountRow.hidden = !(total.discount?.minorUnitsAmount > 0);
  setText(root, '[data-checkout-discount]', total.discount ? `-${formatted(total.discount)}` : '');

  // Hosted shows a Tax row on taxable sessions, with a prompt until the
  // customer's address is known.
  const taxRow = root.querySelector<HTMLElement>('[data-checkout-tax-row]')!;
  const taxStatus = session.tax?.status;
  taxRow.hidden = !taxEnabled;
  setText(
    root,
    '[data-checkout-tax]',
    taxStatus === 'requires_billing_address' ? 'Enter address to calculate' : formatted(total.taxExclusive),
  );
}

function renderLineItem(item: any): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'co-line';

  if (item.images?.length) {
    const image = document.createElement('img');
    image.className = 'co-line-image';
    image.src = item.images[0];
    image.alt = '';
    li.appendChild(image);
  }

  const body = document.createElement('div');
  body.className = 'co-line-body';

  const top = document.createElement('div');
  top.className = 'co-line-top';
  const name = document.createElement('span');
  name.textContent = item.name;
  const amount = document.createElement('span');
  amount.textContent = formatted(item.total || item.subtotal);
  top.append(name, amount);
  body.appendChild(top);

  if (item.description) {
    const description = document.createElement('div');
    description.className = 'co-line-description';
    description.textContent = item.description;
    body.appendChild(description);
  }

  if (item.quantity > 1) {
    const meta = document.createElement('div');
    meta.className = 'co-line-meta';
    meta.textContent = `Qty ${item.quantity}`;
    body.appendChild(meta);
  }

  li.appendChild(body);
  return li;
}

function renderRegistrationSummary(root: HTMLElement, session: any) {
  const lineItems: any[] = session.lineItems || [];
  const total = session.total || {};
  const offersCurrencyChoice = session.currencyOptions?.length > 0;
  const taxEnabled = root.dataset.taxEnabled === 'true';
  const discounted = total.discount?.minorUnitsAmount > 0;

  root.querySelector<HTMLElement>('[data-checkout-currency]')!.hidden = !offersCurrencyChoice;

  const image = root.querySelector<HTMLImageElement>('[data-checkout-hero-image]')!;
  const imageSrc = lineItems[0]?.images?.[0];
  if (imageSrc && image.getAttribute('src') !== imageSrc) image.src = imageSrc;
  image.hidden = !imageSrc;

  const list = root.querySelector<HTMLElement>('[data-checkout-lines]')!;
  list.replaceChildren(...lineItems.map(renderRegistrationLineItem));

  // With nothing to add or take off, the line item already is the total.
  root.querySelector<HTMLElement>('[data-checkout-breakdown]')!.hidden = !taxEnabled && !discounted;
  setText(root, '[data-checkout-subtotal]', formatted(total.subtotal));
  setText(root, '[data-checkout-total-due]', formatted(total.total));

  root.querySelector<HTMLElement>('[data-checkout-discount-row]')!.hidden = !discounted;
  setText(root, '[data-checkout-discount]', total.discount ? `-${formatted(total.discount)}` : '');

  // The Tax row prompts for an address until the customer's is known.
  const taxStatus = session.tax?.status;
  root.querySelector<HTMLElement>('[data-checkout-tax-row]')!.hidden = !taxEnabled;
  setText(
    root,
    '[data-checkout-tax]',
    taxStatus === 'requires_billing_address' ? 'Enter address to calculate' : formatted(total.taxExclusive),
  );
}

function renderRegistrationLineItem(item: any): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'co-reg-line';

  const name = document.createElement('span');
  name.textContent = item.quantity > 1 ? `${item.name} × ${item.quantity}` : item.name;
  const amount = document.createElement('span');
  amount.textContent = formatted(item.total || item.subtotal);
  li.append(name, amount);
  return li;
}

function formatted(value: FormattedAmount | undefined): string {
  return value?.amount ?? '';
}

function setText(root: HTMLElement, selector: string, text: string) {
  const el = root.querySelector<HTMLElement>(selector);
  if (el) el.textContent = text;
}

// Errors on this page are otherwise only ever seen in the customer's console.
// Stripe's codes rather than its messages, so the events group in Blazer.
// ahoy sends by sendBeacon, so a Pay press survives Stripe's redirect away.
function track(root: HTMLElement, name: string, properties: Record<string, unknown> = {}) {
  ahoy.track(name, { microchip_id: root.dataset.microchipId, ...properties });
}

function trackPaymentError(root: HTMLElement, paymentMethod: string | undefined, error: any) {
  track(root, 'Checkout Payment Error', {
    payment_method: paymentMethod,
    error_type: error?.type,
    error_code: error?.code,
    decline_code: error?.decline_code,
  });
}

function setLoading(root: HTMLElement, loading: boolean) {
  root.querySelector<HTMLButtonElement>('[data-checkout-pay]')!.disabled = loading;
  root.querySelector<HTMLElement>('[data-checkout-spinner]')!.hidden = !loading;
}

function showError(root: HTMLElement, message: string) {
  const el = root.querySelector<HTMLElement>('[data-checkout-error]')!;
  el.textContent = message;
  el.hidden = false;
}

function hideError(root: HTMLElement) {
  root.querySelector<HTMLElement>('[data-checkout-error]')!.hidden = true;
}
